/**
 * Protected Route Component
 */

import { Navigate, useLocation } from 'react-router-dom';

import { PageLoading } from '../../components/ui';
import { useAuthStore } from '../../stores';
import type { UserRole } from '../../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserRole[];
  permissions?: { resource: string; action: string }[];
}

export function ProtectedRoute({ children, roles, permissions }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user, hasAnyRole, canAccess, isLoading } = useAuthStore();

  // Show loading while checking auth
  if (isLoading) {
    return <PageLoading message="Authentifizierung wird geprüft..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (roles && roles.length > 0) {
    if (!hasAnyRole(roles)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check permission requirements
  if (permissions && permissions.length > 0) {
    const hasAllPermissions = permissions.every((p) => canAccess(p.resource, p.action));
    if (!hasAllPermissions) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
